import { isExpectedApiError } from "@app/config/sentry";
import { isAxiosApiError } from "@base/lib/utils";
import { summarizeDailyLogSearchQueryKey } from "@features/project-detail/model/dailyLogSearchQueries";
import * as Sentry from "@sentry/react";
import { MutationCache, QueryCache, QueryClient, type QueryKey } from "@tanstack/react-query";
import { isAxiosError } from "axios";

/** 검색 query key는 조건 원문(검색어 등) 대신 안전한 요약으로 기록한다. 다른 key는 그대로 둔다 */
function toLoggableQueryKey(queryKey: QueryKey): readonly unknown[] {
  return summarizeDailyLogSearchQueryKey(queryKey) ?? queryKey;
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      retry: (failureCount, error) => {
        if (isAxiosError(error)) {
          if (error.code === "ERR_CANCELED") return false;
          if (error.response?.status && error.response.status < 500) return false;
        }
        return failureCount < 1;
      },
      refetchOnWindowFocus: false,
      networkMode: "online",
    },
    mutations: {
      networkMode: "online",
    },
  },
  queryCache: new QueryCache({
    onError: (error, query) => {
      const status = isAxiosError(error) ? error.response?.status : undefined;
      const queryKey = toLoggableQueryKey(query.queryKey);

      Sentry.addBreadcrumb({
        category: "query",
        message: `Query failed: ${JSON.stringify(queryKey)}`,
        level: "error",
        data: { queryKey, status },
      });

      if (!isExpectedApiError(error)) {
        Sentry.captureException(error, {
          tags: { source: "query" },
          extra: {
            queryKey,
            status,
            data: isAxiosApiError(error) ? error.response.data : error.message,
          },
        });
      }
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      const status = isAxiosError(error) ? error.response?.status : undefined;
      const mutationKey = mutation.options.mutationKey ?? "unknown";

      Sentry.addBreadcrumb({
        category: "mutation",
        message: `Mutation failed: ${JSON.stringify(mutationKey)}`,
        level: "error",
        data: { mutationKey, status },
      });

      if (!isExpectedApiError(error)) {
        Sentry.captureException(error, {
          tags: { source: "mutation" },
          extra: { mutationKey, status, data: isAxiosApiError(error) ? error.response.data : error.message },
        });
      }
    },
  }),
});
